import SkeletonLoader from "@/components/layout/skeleton-loader/skeleton-loader";
import { type UseQueryResult } from "@tanstack/react-query";
import styles from "./standard-token-window.module.scss";
import StandardTokenCard from "@/features/bridge/components/token-selector/selector-interface/standard-token/standard-token-card";
import type { StandardTokenType } from "@/features/bridge/types/token-selector.types";
import TokensNotFound from "@/features/bridge/components/token-selector/selector-interface/tokens-not-found";
import { useTokenFilterStore } from "@/features/bridge/stores/token-filter.store";
import { useTokenEligibility } from "@/features/bridge/hooks/useTokenEligibility.hook";

interface Props {
  query: UseQueryResult<StandardTokenType[] | null, Error>;
}

// Component to check eligibility and conditionally render token
const FilteredTokenCard = ({ token, query }: { token: StandardTokenType; query: UseQueryResult<StandardTokenType[] | null, Error> }) => {
  const { showOnlyBridgeableTokens } = useTokenFilterStore();
  const eligibility = useTokenEligibility(token.tokenAddress);

  // If filtering is enabled and token is not eligible, don't render
  if (showOnlyBridgeableTokens && !eligibility.isEligible) {
    return null;
  }

  return (
    <StandardTokenCard
      key={token.tokenAddress}
      token={token}
      query={query}
    />
  );
};

const StandardTokenList = ({ query }: Props) => {
  const { showOnlyBridgeableTokens } = useTokenFilterStore();

  // Calculate filtered tokens for display counts
  const filteredTokens = query.data?.filter(token => {
    if (!showOnlyBridgeableTokens) return true;
    // For filtered view, we need to check eligibility
    // Note: This is a simple check, full eligibility is checked in FilteredTokenCard
    return true; // Let FilteredTokenCard handle the filtering
  });

  return (
    <div className={styles.listContainer}>
      {query && query.isFetching
        ? Array.from({ length: 3 }).map((_, i) => <SkeletonLoader key={i} />)
        : query.data?.map((token, i) => (
            <FilteredTokenCard
              key={token.tokenAddress}
              token={token}
              query={query}
            />
          ))}
      {!query.isFetching && !query.data?.length && (
        <TokensNotFound size="small" />
      )}
    </div>
  );
};

export default StandardTokenList;
